import OutlineSection from "./OutlineSection";
import Title from "./Title";
import { useMemo, useRef } from "react";
import uuidv4 from "../../../../../../ui/util/uuidv4";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import classNames from "classnames";
import Button from "../../../../../../ui/Button/Button";
import { ChevronSelector, Plus, Refresh } from "../../../../../../ui/Icon/Icon";
import { graphql } from "../../../../../../__generated__/gql";
import Textarea from "../../../../../../ui/Textarea/Textarea";
import { useMutation } from "urql";
import styles from "./OutlineInput.module.scss";
import { useDisclosure } from "../../../../../../ui/util/useDisclosure";
import Modal from "../../../../../../ui/Modal/Modal";
import ConfirmationModal from "../../../../../../ui/Modal/ConfirmationModal";

function ConfirmGenerationModal({
  isOpen,
  regenerate,
  creditsRequired,
  loading,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  regenerate: boolean;
  creditsRequired: number;
  loading: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Confirm ${
        regenerate ? "regeneration" : "generation"
      } of article?`}
    >
      <ConfirmationModal
        onClose={onClose}
        onSubmit={onSubmit}
        loading={loading}
        confirmationText={
          regenerate
            ? `Regenerate (${
                creditsRequired === 0
                  ? "Free"
                  : `${creditsRequired} ${
                      creditsRequired === 1 ? "credit" : "credits"
                    }`
              })`
            : `Generate (${
                creditsRequired === 0
                  ? "Free"
                  : `${creditsRequired} ${
                      creditsRequired === 1 ? "credit" : "credits"
                    }`
              })`
        }
      />
    </Modal>
  );
}

const RegenerateOutlineMutation = graphql(/* GraphQL */ `
  mutation regenerateOutline($id: String!) {
    generateArticle(
      input: {
        id: $id
        regenerateOutline: true
        regenerateContent: false
        regenerateBrief: false
      }
    ) {
      ... on Article {
        id
        status
      }
      ... on InsufficientCreditsError {
        __typename
        message
      }
    }
  }
`);

export type OutlineState = {
  id: string;
  title: string;
  sections: {
    id?: string;
    internalId: string;
    title: string;
    titleDisabled: boolean;
    description?: string;
    isExpanded: boolean;
  }[];
};

function OutlineInput({
  processing,
  generating,
  regenerating,
  creditsRequired,
  state,
  set,
  moveNode,
}: {
  processing?: boolean;
  generating?: boolean;
  regenerating?: boolean;
  creditsRequired: number;
  state: OutlineState;
  set: (value: React.SetStateAction<OutlineState>, replace?: boolean) => void;
  moveNode: (nodeId: string, beforeNodeId: string) => void;
}) {
  const disabled = processing;
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const [regenerateOutlineResponse, regenerateOutline] = useMutation(
    RegenerateOutlineMutation,
  );
  const [confirmGenerationModalOpened, confirmGenerationModal] =
    useDisclosure();

  const titleRef = useRef<HTMLInputElement>(null);
  const sectionRefs = useRef<{
    [id: string]: {
      title: HTMLInputElement | null;
      description: HTMLTextAreaElement | null;
    };
  }>({});

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (
      over &&
      active.id !== over.id &&
      typeof over.id === "string" &&
      typeof active.id === "string"
    ) {
      moveNode(active.id, over.id);
    }
  }

  const isAllExpanded = useMemo(
    () => state.sections.every((section) => section.isExpanded),
    [state.sections],
  );

  function expandAllSections() {
    set((previous) => ({
      ...previous,
      sections: previous.sections.map((section) => ({
        ...section,
        isExpanded: true,
      })),
    }));
  }

  function collapseAllSections() {
    set((previous) => ({
      ...previous,
      sections: previous.sections.map((section) => ({
        ...section,
        isExpanded: false,
      })),
    }));
  }

  return (
    <>
      <ConfirmGenerationModal
        isOpen={confirmGenerationModalOpened}
        loading={regenerateOutlineResponse.fetching}
        creditsRequired={creditsRequired}
        regenerate={!!state.sections.length}
        onSubmit={async () => {
          await regenerateOutline({ id: state.id });
          confirmGenerationModal.close();
        }}
        onClose={confirmGenerationModal.close}
      />
      <DndContext
        onDragEnd={handleDragEnd}
        sensors={sensors}
        collisionDetection={closestCenter}
      >
        <SortableContext
          items={state.sections.map((section) => section.internalId)}
          id="internalId"
          strategy={verticalListSortingStrategy}
        >
          <div
            className={classNames(styles.outline, {
              [styles.disabled]: disabled,
            })}
          >
            {generating ? (
              <Textarea
                disabled
                loading
                unstyled
                loadingMessage={regenerating ? "Regenerating" : "Generating"}
              />
            ) : (
              <>
                <div className={styles.outlineInner}>
                  <Title
                    index={0}
                    titleRef={titleRef}
                    disabled={disabled}
                    value={state.title}
                    onChange={(value) => {
                      set((previous) => ({ ...previous, title: value }));
                    }}
                    focusNext={() => {
                      const firstSection = state.sections[0];

                      if (firstSection) {
                        sectionRefs.current[
                          firstSection.internalId
                        ].title?.focus();

                        setTimeout(() => {
                          if (titleRef.current) {
                            sectionRefs.current[
                              firstSection.internalId
                            ].title?.setSelectionRange(
                              titleRef.current.selectionStart,
                              titleRef.current.selectionStart,
                            );
                          }
                        });
                      }
                    }}
                    headingLevel={1}
                  />
                  {!!state.sections.length && (
                    <div className={styles.sections}>
                      {state.sections.map((section, index) => (
                        <OutlineSection
                          index={index}
                          disabled={disabled}
                          titleRef={
                            sectionRefs.current[section.internalId]?.title ??
                            null
                          }
                          setTitleRef={(ref) =>
                            (sectionRefs.current[section.internalId] = {
                              ...sectionRefs.current[section.internalId],
                              title: ref,
                            })
                          }
                          descriptionRef={
                            sectionRefs.current[section.internalId]
                              ?.description ?? null
                          }
                          setDescriptionRef={(ref) => {
                            sectionRefs.current[section.internalId] = {
                              ...sectionRefs.current[section.internalId],
                              description: ref,
                            };
                          }}
                          key={section.internalId}
                          value={section}
                          onChange={(value, replace) => {
                            set((previous) => {
                              const sections = [...previous.sections];
                              sections[index] = {
                                ...sections[index],
                                ...value,
                              };

                              return { ...previous, sections };
                            }, replace);
                          }}
                          appendSection={() => {
                            const newId = uuidv4();

                            set((previous) => ({
                              ...previous,
                              sections: [
                                ...previous.sections.slice(0, index + 1),
                                {
                                  internalId: newId,
                                  title: "",
                                  titleDisabled: false,
                                  description: "",
                                  isExpanded: true,
                                },
                                ...previous.sections.slice(index + 1),
                              ],
                            }));

                            setTimeout(() => {
                              sectionRefs.current[newId].title?.focus();
                            });
                          }}
                          deleteSection={() => {
                            set((previous) => ({
                              ...previous,
                              sections: [
                                ...previous.sections.slice(0, index),
                                ...previous.sections.slice(index + 1),
                              ],
                            }));
                          }}
                          focusNext={() => {
                            if (index < state.sections.length - 1) {
                              const nextSection = state.sections[index + 1];
                              const currentSection = state.sections[index];
                              const currentSectionRef =
                                sectionRefs.current[currentSection.internalId]
                                  .title;

                              if (nextSection) {
                                sectionRefs.current[
                                  nextSection.internalId
                                ].title?.focus();

                                setTimeout(() => {
                                  if (currentSectionRef) {
                                    sectionRefs.current[
                                      nextSection.internalId
                                    ].title?.setSelectionRange(
                                      currentSectionRef.selectionStart,
                                      currentSectionRef.selectionStart,
                                    );
                                  }
                                });
                              }
                            }
                          }}
                          focusPrevious={() => {
                            const currentSection = state.sections[index];
                            const currentSectionRef =
                              sectionRefs.current[currentSection.internalId]
                                .title;

                            if (index > 0) {
                              const previousSection = state.sections[index - 1];

                              if (previousSection) {
                                if (previousSection.isExpanded) {
                                  sectionRefs.current[
                                    previousSection.internalId
                                  ].description?.focus();

                                  setTimeout(() => {
                                    if (currentSectionRef) {
                                      sectionRefs.current[
                                        previousSection.internalId
                                      ].description?.setSelectionRange(-1, -1);
                                    }
                                  });
                                } else {
                                  sectionRefs.current[
                                    previousSection.internalId
                                  ].title?.focus();

                                  setTimeout(() => {
                                    if (currentSectionRef) {
                                      sectionRefs.current[
                                        previousSection.internalId
                                      ].title?.setSelectionRange(
                                        currentSectionRef.selectionStart,
                                        currentSectionRef.selectionStart,
                                      );
                                    }
                                  });
                                }
                              }
                            } else {
                              titleRef.current?.focus();

                              setTimeout(() => {
                                if (currentSectionRef) {
                                  titleRef.current?.setSelectionRange(
                                    currentSectionRef.selectionStart,
                                    currentSectionRef.selectionStart,
                                  );
                                }
                              });
                            }
                          }}
                        />
                      ))}
                    </div>
                  )}
                  {!disabled && (
                    <div className={styles.addNewSectionButton}>
                      <Button
                        variant="outline"
                        leftIcon={<Plus />}
                        size="small"
                        onClick={() => {
                          const newId = uuidv4();

                          set((previous) => ({
                            ...previous,
                            sections: [
                              ...previous.sections,
                              {
                                internalId: newId,
                                title: "",
                                titleDisabled: false,
                                description: "",
                                isExpanded: true,
                              },
                            ],
                          }));

                          setTimeout(() => {
                            sectionRefs.current[newId].title?.focus();
                          });
                        }}
                      >
                        Add new section
                      </Button>
                    </div>
                  )}
                </div>
                <div className={styles.footer}>
                  <Button
                    onClick={() =>
                      isAllExpanded
                        ? collapseAllSections()
                        : expandAllSections()
                    }
                    leftIcon={<ChevronSelector />}
                    variant="outline"
                  >
                    {isAllExpanded ? "Collapse" : "Expand"} all descriptions
                  </Button>
                  <Button
                    onClick={confirmGenerationModal.open}
                    leftIcon={<Refresh />}
                    variant="outline"
                    loading={regenerateOutlineResponse.fetching}
                    disabled={regenerateOutlineResponse.fetching}
                  >
                    {state.sections.length ? "Regenerate" : "Generate"} outline
                  </Button>
                </div>
              </>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </>
  );
}

export default OutlineInput;
